import { bootstrapApplication } from '@angular/platform-browser';
import { enableProdMode, importProvidersFrom, isDevMode, inject, ApplicationConfig } from '@angular/core';
import { 
  RouteReuseStrategy, provideRouter, withPreloading, PreloadAllModules, 
  withComponentInputBinding, withDebugTracing
} from '@angular/router';
import { 
  provideHttpClient, HttpClient, withFetch, withInterceptors, 
  HttpContextToken, HttpHeaders, HttpParams, 
} from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { register } from 'swiper/element/bundle';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { environment } from './environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { HttpRequest, HttpHandlerFn, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, tap, lastValueFrom, from, filter } from 'rxjs';

import '@angular/common/locales/global/es';
import '@angular/common/locales/global/ca';
import '@angular/common/locales/global/en'
import { StorageService } from './app/storage.service';

/** Links per a les botigues:
 * https://landing.previntegral.com/previnapp-politica-de-privacitat/
 * https://landing.previntegral.com/previnapp-politica-de-privacidad/
 */

// injecto un mapping de strings (barreja de union types) a colors
// per mantenir-la constant per a tota la aplicació
export abstract class ColorsTipus {
  // riscos
  IN!: string;
  I!: string;
  M!: string;
  TO!: string;
  T!: string;
  // estats de les mesures
  no_plan!: string;
  unfulfilled!: string;
  pending!: string;
  checked!: string;
  // estat_art19
  NO_FORMAT!: string;
  'CURS-EXTERN'!:string;
  CADUCAT!: string;
  FORMAT!: string;
  PENDENT!: string;  // equival a NO_FORMAT
  // estat_reco
  NO_PLANIFICAT!:string;
  PLANIFICAT!:string;
  NO_EXECUTAT!:string;
  // estat_ipe
  SIGNATS!:string;
  ENTREGATS!:string;
  'NO-ENTREGATS'!:string;
  // aptitud
  0!: string;
  1!: string;
  2!: string;
  3!: string;
  4!: string;
  6!: string;
  7!: string;
  8!: string;
  10!: string;
  11!: string;
  12!: string;
  13!: string;
};
const colorsTipus:Record<string, string> = {
  'IN': 'danger',
  'I': 'secondary',
  'M': 'warning',
  'TO': 'tertiary',
  'T': 'success',
  '10': 'danger',
  '20': 'secondary',
  '30': 'warning',
  '40': 'tertiary',
  '50': 'success',
  '60': 'success',
  'NO_FORMAT': 'danger',
  'CURS-EXTERN': 'success',
  'CADUCAT': 'warning',
  'FORMAT': 'success',
  'PENDENT': 'danger',
  'NO_PLANIFICAT':'danger',
  'PLANIFICAT':'success',
  'NO_EXECUTAT':'danger',
  'SIGNATS':'success',
  'ENTREGATS':'warning',
  'NO-ENTREGATS':'danger',
  '0':'danger',
  '1': 'success',
  '2': 'danger',
  '3': 'warning',
  '4': 'danger',
  '6': 'warning',
  '7': 'danger',
  '8': 'warning',
  '11': 'success',
  '12': 'danger',
  '13': 'danger',
  'reco_caducat': 'warning'
};

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// NOTA: no funciona amb HttpContextToken (es penja en fer new HttpContextToken().set(...) )
// ho substitueixo per un header custom que elimino abans de fer la petició

// export const CACHING_K = new HttpContextToken<string>(() => '');

export function cachingInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const storage = inject(StorageService);
  const cParams = req.params;
  const key = req.headers.get('_cache_key_'); // const key = req.context.get(CACHING_K);
  
  if (!!key ) {//cParams.get('access_token') && req.method == 'GET') {
    let _cache:Cache;
    const reqClone = req.clone({
      headers: req.headers.delete('_cache_key_')
    });
    
    // els paràmetres, excepte l'access_token
    const pnames = cParams.keys().filter(k=>k!='access_token');
    let nParams = new HttpParams();
    pnames.forEach(k=>nParams = nParams.set(k, cParams.get(k)!));
    
    const urlWithParams = `${req.url}?${nParams.toString()}`;

    return from(
      storage.ready.then(
        ()=>Promise.resolve(storage.getCache(key))
      ).then(gotCache=>{
        return caches.open('_previnapp_cache_').then(
          (cache)=>{
            _cache = cache;
            if (!gotCache) return _cache.delete(urlWithParams).then(()=>_cache.match(urlWithParams))
            else return _cache.match(urlWithParams);
        })
      }).then(
        (response) => {
        if (response && response.body) {
          const hdrs = new HttpHeaders();
          response.headers.forEach((val,key)=>hdrs.append(key,val))
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let bodyResult = ""
          
          const processText = ({ done, value }:ReadableStreamReadResult<Uint8Array>): Promise<any>=>{
            if (done) {
              return Promise.resolve(JSON.parse(bodyResult));
            }
            bodyResult += decoder.decode(value);
        
            // Read some more, and call this function again
            return reader.read().then(processText);
          }
          return reader.read().then(processText).then(result=>{
            return new HttpResponse({
              body: result,
              headers: hdrs,
              status: response.status,
              statusText: response.statusText,
              url: req.url
            });
          });
        }
        const resp$ = next(reqClone).pipe(
          tap(event => {
            if (event.type === HttpEventType.Response) {
              const rresp = event.clone() as HttpResponse<unknown>;
              const hdrs = new Headers();
              rresp.headers.keys().forEach(key=>hdrs.append(key, rresp.headers.get(key)!));
              const opts = { status: rresp.status, statusText: rresp.statusText, headers: hdrs };
              if ( rresp.status < 400 && rresp.status>0 ) {
                _cache.delete(urlWithParams).then(
                  ()=>_cache.put(urlWithParams, new Response(JSON.stringify(rresp.body) as BodyInit, opts))
                ).then(()=>{
                  storage.setCache(key, key);
                });
              }
            }
          })
        )
        return lastValueFrom(resp$);
      })
    );
  } else {
    // caching has been disabled for this request
    return next(req).pipe(
      filter(event=>(event.type === HttpEventType.Response)),
    );
  }
}

register(); // <- registrem el Swiper element
defineCustomElements(window); // <- Call the element loader before the bootstrapModule/bootstrapApplication call
if (environment.production) {
  enableProdMode()
}
bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ColorsTipus, useValue: colorsTipus },
    provideIonicAngular({
      mode: 'md',
      backButtonText: ''
    }),
    provideRouter(
      routes, 
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      //withDebugTracing()
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([cachingInterceptor])
    ),
    importProvidersFrom( IonicStorageModule.forRoot() ),
    importProvidersFrom( TranslateModule.forRoot({
      loader:{
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'ca'
    })), 
    provideCharts(withDefaultRegisterables()), 
    /*
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,//!isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    /*/
    provideServiceWorker('firebase-messaging-sw.js', {
      enabled: !Capacitor.isNativePlatform(),
      registrationStrategy: 'registerWhenStable:30000'
    })
    //*/
  ],
});
